import React, { createContext, useContext, useState, useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { useScroll } from "@react-three/drei";
import { TILE_POSITIONS } from "../constants/projectsConstants";
import * as THREE from "three";

const CurrentTileContext = createContext({
  currentTileIndex: null,
  currentTile: null,
  isInProjectsZone: false,
});

// Global state for access outside Canvas
let globalCurrentTileIndex = null;
let globalCurrentTile = null;
let globalListeners = new Set();

const notifyListeners = () => {
  globalListeners.forEach((listener) => listener());
};

export const useCurrentTile = () => {
  const context = useContext(CurrentTileContext);
  const [, forceUpdate] = useState({});
  
  useEffect(() => {
    const listener = () => forceUpdate({});
    globalListeners.add(listener);
    return () => globalListeners.delete(listener);
  }, []);
  
  if (context && context.currentTileIndex !== null) {
    return context;
  }
  
  // Fallback to global state if context not available (outside Canvas)
  return {
    currentTileIndex: globalCurrentTileIndex,
    currentTile: globalCurrentTile,
    isInProjectsZone: globalCurrentTileIndex !== null,
  };
};

/**
 * Provider component that tracks the current tile in view
 * Must be used inside Canvas/ScrollControls
 */
export const CurrentTileProvider = ({ children }) => {
  const [currentTileIndex, setCurrentTileIndex] = useState(null);
  const data = useScroll();
  const lastIndexRef = useRef(null);

  useFrame(() => {
    if (!data) return;

    const scroll = data.offset;
    const homeZoneEnd = 2 / 8; // 0.25
    const tilesZoneEnd = 5 / 8; // 0.625
    const transitionBuffer = 0.01;

    const effectiveTilesStart = homeZoneEnd + transitionBuffer;
    const tilesRange = tilesZoneEnd - effectiveTilesStart;

    // Only calculate tile index when in projects zone
    if (scroll > effectiveTilesStart && scroll <= tilesZoneEnd + transitionBuffer) {
      const tilesProgress = THREE.MathUtils.clamp(
        (scroll - effectiveTilesStart) / tilesRange,
        0,
        1,
      );

      const exactTileIndex = tilesProgress * (TILE_POSITIONS.length - 1);
      // Round to nearest tile index for "in position" detection
      const roundedIndex = Math.round(exactTileIndex);
      const clampedIndex = Math.max(0, Math.min(roundedIndex, TILE_POSITIONS.length - 1));
      
      // Only update state if index changed
      if (clampedIndex !== lastIndexRef.current) {
        lastIndexRef.current = clampedIndex;
        setCurrentTileIndex(clampedIndex);
        // Update global state for access outside Canvas
        globalCurrentTileIndex = clampedIndex;
        globalCurrentTile = TILE_POSITIONS[clampedIndex];
        notifyListeners();
      }
    } else {
      // Only update if we were previously in projects zone
      if (lastIndexRef.current !== null) {
        lastIndexRef.current = null;
        setCurrentTileIndex(null);
        // Update global state
        globalCurrentTileIndex = null;
        globalCurrentTile = null;
        notifyListeners();
      }
    }
  });

  const currentTile = currentTileIndex !== null ? TILE_POSITIONS[currentTileIndex] : null;

  const value = {
    currentTileIndex,
    currentTile,
    isInProjectsZone: currentTileIndex !== null,
  };

  return (
    <CurrentTileContext.Provider value={value}>
      {children}
    </CurrentTileContext.Provider>
  );
};

export default CurrentTileContext;

